import { MaterialIcons } from "@expo/vector-icons";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import { useTheme } from "react-native-elements";
import { clearFlag, hasFlag } from "../backend/Flags";
import { PlayerContext } from "../backend/PlayerContext";
import { calculatePlayerMaxHP } from "../backend/exploration/CombatStats";
import {
  endExploration,
  getCurrentDungeonFloor,
  getCurrentEnemyHP,
  getCurrentPlayerHP,
  getCurrentSummonHP,
  getExplorationStatus,
  getSuccessfulExplorationsForFloor,
  pauseExploration,
  resumeExploration,
  startBossExploration,
  startExplorationOnFloor,
} from "../backend/exploration/Exploration";
import { ExplorationStatus } from "../backend/exploration/ExplorationStatus";
import { hasCurrentActiveSummon } from "../backend/exploration/Summoning";
import { Dungeon } from "../backend/exploration/dungeons/Dungeon";
import { DungeonFloor } from "../backend/exploration/dungeons/DungeonFloor";
import {
  getUnlockedDungeons,
  isDungeonFloorCompletedThisRun,
} from "../backend/exploration/dungeons/Dungeons";
import { getCurrentEnemy } from "../backend/exploration/enemies/Enemies";
import { getCurrentSummon } from "../backend/exploration/summons/Summons";
import { formatNumber } from "../utils/FormattingUtils";
import { EnemyActionDisplay } from "./exploration/EnemyActionDisplay";
import { EnemyHPBar } from "./exploration/EnemyHPBar";
import { EnemySpriteTile } from "./exploration/EnemySpriteTile";
import { ExplorationBottomComponent } from "./exploration/ExplorationBottomComponent";
import { ExplorationProgressDisplay } from "./exploration/ExplorationProgressDisplay";
import { PlayerActionDisplay } from "./exploration/PlayerActionDisplay";
import { PlayerHPBar } from "./exploration/PlayerHPBar";
import { PlayerMessageLog } from "./exploration/PlayerMessageLog";
import { SummonActionDisplay } from "./exploration/SummonActionDisplay";
import { SummonHPBar } from "./exploration/SummonHPBar";
import { SummonSpriteTile } from "./exploration/SummonSpriteTile";
import { AccessibilityContext } from "./utility/AccessibilityContext";
import { BottomResourceViewer } from "./utility/BottomResourceViewer";
import { Button } from "./utility/Button";
import CategoryContainer from "./utility/CategoryContainer";
import Divider from "./utility/Divider";
import { KeyboardListener } from "./utility/KeyboardListener";
import { TemporaryEffectsViewer } from "./utility/TemporaryEffectsViewer";
import { Text } from "./utility/Text";
import { UIContext } from "./utility/UIContext";
import { VisibilityContext } from "./utility/VisibilityContext";

function ExplorationRow(props: { floor: DungeonFloor }) {
  const floor = props.floor;
  const playerContext = useContext(PlayerContext);
  const isBossUnlocked =
    floor.canFightBoss(playerContext) && floor.getBoss(playerContext) != null;
  const isFloorCompleted = isDungeonFloorCompletedThisRun(playerContext, floor);
  const theme = useTheme();
  return (
    <View>
      <View style={styles.noneRow}>
        <View>
          <Text style={styles.noneFloorText}>{floor.getFloorName()}</Text>
          <View style={styles.noneStatusRow}>
            <Text>Status: </Text>
            {isBossUnlocked ? (
              <MaterialIcons
                name="check"
                size={24}
                style={styles.noneIcon}
                color={
                  isFloorCompleted
                    ? theme.theme.colors.primary
                    : theme.theme.colors.black
                }
              />
            ) : (
              <MaterialIcons
                name="lock-open"
                size={24}
                style={styles.noneIcon}
                color={theme.theme.colors.grey0}
              />
            )}
            <Text>
              {isFloorCompleted
                ? "Completed"
                : isBossUnlocked
                ? "Boss Unlocked"
                : "Open for Exploration"}
            </Text>
          </View>
        </View>
        <Button
          title="Explore"
          onPress={() =>
            playerContext.apply((state) =>
              startExplorationOnFloor(state, floor),
            )
          }
        />
      </View>
    </View>
  );
}

function ExplorationDungeon(props: { dungeon: Dungeon }) {
  const playerContext = useContext(PlayerContext);
  return (
    <CategoryContainer title={props.dungeon.getName()}>
      <View style={styles.noneDungeonContentsContainer}>
        {props.dungeon
          .getDungeonFloors()
          .filter((floor) => floor.isUnlocked(playerContext))
          .map((floor) => {
            return <ExplorationRow key={floor.getId()} floor={floor} />;
          })}
      </View>
    </CategoryContainer>
  );
}

function ExplorationNone() {
  const playerContext = useContext(PlayerContext);
  const allDungeons = getUnlockedDungeons(playerContext);
  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.noneContent}
    >
      <View style={styles.topRow}>
        <Text h2>Exploration</Text>
      </View>
      <Text>Select a destination.</Text>
      {allDungeons.map((dungeon) => {
        return <ExplorationDungeon key={dungeon.getId()} dungeon={dungeon} />;
      })}
    </ScrollView>
  );
}

function ExplorationMain() {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const explorationStatus = getExplorationStatus(playerContext);

  const dungeonFloor = getCurrentDungeonFloor(playerContext);
  const dungeonTitle = dungeonFloor.getFloorName();
  const currentEnemySprite =
    explorationStatus == ExplorationStatus.Combat &&
    getCurrentEnemy(playerContext).getPicture(playerContext);
  const toUnlockBoss =
    dungeonFloor.getExplorationSuccessesForBoss(playerContext) -
    getSuccessfulExplorationsForFloor(playerContext, dungeonFloor.getId());
  const currentEnemyName =
    explorationStatus == ExplorationStatus.Combat &&
    getCurrentEnemy(playerContext).getName();

  const checkmark =
    getSuccessfulExplorationsForFloor(playerContext, dungeonFloor.getId()) >=
    dungeonFloor.getExplorationSuccessesForBoss(playerContext);
  const hasBoss = dungeonFloor.getBoss(playerContext) != null;
  const canFightBoss = dungeonFloor.canFightBoss(playerContext);
  const canMoveToNextFloor = dungeonFloor
    .getNextFloor()
    ?.isUnlocked(playerContext);

  const fightingBoss =
    explorationStatus == ExplorationStatus.Combat &&
    getCurrentEnemy(playerContext).isBoss();

  const isExplorationPaused = hasFlag(playerContext, "exploration_paused");
  const togglePauseExploration = useCallback(() => {
    playerContextRef.current.apply((state) =>
      hasFlag(state, "exploration_paused")
        ? resumeExploration(state)
        : pauseExploration(state),
    );
  }, []);
  const pauseExplorationButton = useMemo(
    () => (
      <Button
        containerStyle={styles.pauseExplorationButton}
        titleStyle={styles.smallButtonTitle}
        title={isExplorationPaused ? "Resume Exploration" : "Pause Exploration"}
        onPress={togglePauseExploration}
      />
    ),
    [isExplorationPaused, togglePauseExploration],
  );

  const explorationStatusContainer = useMemo(() => {
    let explorationStatusContainer = null;
    if (explorationStatus == ExplorationStatus.Combat) {
      explorationStatusContainer = (
        <ScrollView
          centerContent={false}
          style={styles.explorationStatusContainer}
        >
          <Text h3>{currentEnemyName}</Text>
          <EnemyHPBar />
          <EnemyActionDisplay />
          <TemporaryEffectsViewer
            isEnemy={true}
            accessibilityHeader="Enemy Buffs"
          />
        </ScrollView>
      );
    } else if (explorationStatus == ExplorationStatus.Exploring) {
      explorationStatusContainer = (
        <View style={styles.explorationStatusContainer}>
          <Text h3>Exploring...</Text>
          <ExplorationProgressDisplay />
          {pauseExplorationButton}
        </View>
      );
    } else if (explorationStatus == ExplorationStatus.ExploringBoss) {
      explorationStatusContainer = (
        <View style={styles.explorationStatusContainer}>
          <Text h3>Preparing for Boss...</Text>
          <ExplorationProgressDisplay />
          {pauseExplorationButton}
        </View>
      );
    } else if (explorationStatus == ExplorationStatus.Reviving) {
      explorationStatusContainer = (
        <View style={styles.explorationStatusContainer}>
          <Text h3>Recovering...</Text>
          <ActivityIndicator />
        </View>
      );
    }
    return explorationStatusContainer;
  }, [explorationStatus, currentEnemyName, pauseExplorationButton]);

  const hasActiveSummon = hasCurrentActiveSummon(playerContext);
  const currentSummonSprite =
    hasActiveSummon && getCurrentSummon(playerContext)?.getPicture();

  const isFloorCompleted = isDungeonFloorCompletedThisRun(
    playerContext,
    dungeonFloor,
  );
  const { theme } = useTheme();

  const backdropColor =
    explorationStatus == ExplorationStatus.Combat &&
    getCurrentEnemy(playerContext).getBackdropColor();

  const accessibilityContext = useContext(AccessibilityContext);

  const visible = useContext(VisibilityContext);
  useEffect(() => {
    playerContextRef.current.apply(clearFlag("combat_paused_magic_tap"));
    return () =>
      playerContextRef.current.apply(clearFlag("combat_paused_magic_tap"));
  }, [visible]);

  const uiContext = useContext(UIContext);
  const currentEventOccurrenceId = uiContext.getUIState(
    "current_event_occurrence_id",
  );
  const currentExplorationBottomTab = uiContext.getUIState(
    "exploration_bottom_component_tab",
  );

  useEffect(() => {
    if (visible && currentEventOccurrenceId == null) {
      if (explorationStatus === ExplorationStatus.Exploring) {
        accessibilityContext.announceForAccessibility("Exploring");
      }

      if (explorationStatus === ExplorationStatus.ExploringBoss) {
        accessibilityContext.announceForAccessibility("Preparing for boss");
      }

      if (explorationStatus === ExplorationStatus.Combat) {
        accessibilityContext.announceForAccessibility(
          "Fighting " + currentEnemyName,
        );
      }

      if (explorationStatus === ExplorationStatus.Reviving) {
        accessibilityContext.announceForAccessibility(
          "You were defeated. Recovering from injuries...",
        );
      }
    }
  }, [
    explorationStatus,
    accessibilityContext,
    visible,
    currentEventOccurrenceId,
  ]);

  const [lastAnnounced, setLastAnnounced] = useState("");

  const onKeyboard = useCallback(
    (ev: KeyboardEvent) => {
      if (!visible) {
        return;
      }

      const state = playerContextRef.current;

      if (ev.shiftKey && ev.ctrlKey && ev.key == "Z") {
        const inCombat =
          getExplorationStatus(state) == ExplorationStatus.Combat;
        const hasSummon = hasCurrentActiveSummon(state);
        if (lastAnnounced === "player" && hasSummon) {
          const currentHP = getCurrentSummonHP(state);
          const maxHP = getCurrentSummon(state)?.getMaxHP(state) ?? 0;
          accessibilityContext.announceForAccessibility(
            `Summon HP: ${formatNumber(Math.ceil(currentHP), {
              showDecimals: false,
            })}/${formatNumber(Math.ceil(maxHP), {
              showDecimals: false,
            })}`,
          );
          setLastAnnounced("summon");
        } else if (
          (lastAnnounced === "player" ||
            (hasSummon && lastAnnounced === "summon")) &&
          inCombat
        ) {
          const currentHP = getCurrentEnemyHP(state);
          const maxHP = getCurrentEnemy(state).getMaxHP(state);
          accessibilityContext.announceForAccessibility(
            `Enemy HP: ${formatNumber(Math.ceil(currentHP), {
              showDecimals: false,
            })}/${formatNumber(Math.ceil(maxHP), {
              showDecimals: false,
            })}`,
          );
          setLastAnnounced("enemy");
        } else {
          accessibilityContext.announceForAccessibility(
            `Player HP: ${formatNumber(Math.ceil(getCurrentPlayerHP(state)), {
              showDecimals: false,
            })}/${formatNumber(Math.ceil(calculatePlayerMaxHP(state)), {
              showDecimals: false,
            })}`,
          );
          setLastAnnounced("player");
        }
      }
    },
    [visible, lastAnnounced],
  );

  const topContainer = useMemo(
    () => (
      <View>
        <View style={[styles.row, styles.enemyContainer]}>
          <EnemySpriteTile
            sprite={currentEnemySprite}
            backdropColor={backdropColor}
          />
          {explorationStatusContainer}
        </View>
        <Divider />
        <View style={[styles.row, styles.playerContainer]}>
          <View style={{ flex: 1, marginRight: 4 }}>
            <PlayerHPBar />
          </View>
          <PlayerActionDisplay />
        </View>
        {hasActiveSummon && (
          <View style={[styles.row, styles.summonContainer]}>
            <SummonSpriteTile sprite={currentSummonSprite} />
            <View style={{ flex: 1, marginLeft: 4, marginRight: 4 }}>
              <SummonHPBar />
            </View>
            <SummonActionDisplay />
          </View>
        )}
        <View style={[styles.row, styles.runAway]}>
          <View style={[styles.row, styles.unlockBossContainer]}>
            {checkmark && (
              <MaterialIcons
                name="check"
                size={24}
                style={styles.checkmark}
                color={
                  isFloorCompleted ? theme.colors.primary : theme.colors.black
                }
              />
            )}
            <View style={styles.unlockBossTextContainer}>
              <Text doNotScaleFonts={true} umberOfLines={2}>
                {isFloorCompleted
                  ? "Floor complete!"
                  : canFightBoss
                  ? "Boss unlocked"
                  : hasBoss
                  ? `To unlock boss: ${toUnlockBoss}`
                  : ""}
              </Text>
            </View>
          </View>
          <View style={styles.row}>
            <Button
              containerStyle={styles.exitButtonContainer}
              title="Exit"
              titleStyle={styles.smallButtonTitle}
              onPress={() => playerContextRef.current.apply(endExploration)}
            />
            <Button
              containerStyle={styles.exitButtonContainer}
              title="Next Floor"
              titleStyle={styles.smallButtonTitle}
              onPress={() =>
                playerContextRef.current.apply((state) =>
                  startExplorationOnFloor(
                    state,
                    getCurrentDungeonFloor(
                      state,
                    ).getNextFloor() as DungeonFloor,
                  ),
                )
              }
              disabled={!canMoveToNextFloor}
            />
            <Button
              title="Fight Boss"
              titleStyle={styles.smallButtonTitle}
              onPress={() =>
                playerContextRef.current.apply(startBossExploration)
              }
              disabled={!canFightBoss || fightingBoss}
            />
          </View>
        </View>
      </View>
    ),
    [
      currentEnemySprite,
      explorationStatusContainer,
      toUnlockBoss,
      checkmark,
      canFightBoss,
      fightingBoss,
      hasBoss,
      playerContextRef,
      startBossExploration,
      endExploration,
      canMoveToNextFloor,
      hasActiveSummon,
      currentSummonSprite,
      isFloorCompleted,
      backdropColor,
      theme,
    ],
  );

  const { height, width } = useWindowDimensions();

  return useMemo(
    () => (
      <View style={styles.wholeContainer}>
        <View style={styles.container}>
          <View style={[styles.topRow]}>
            <Text h2>Exploration - {dungeonTitle}</Text>
          </View>
          <View style={styles.restOfScreen}>
            {topContainer}
            <View style={styles.playerMessageLogContainer}>
              {currentExplorationBottomTab == "" && (
                <View
                  style={styles.accessibilityHidden}
                  accessible={true}
                  accessibilityLabel="Exploration Log Tab"
                  accessibilityRole="header"
                  accessibilityElementsHidden={false}
                />
              )}
              <PlayerMessageLog />
            </View>
            {width < 640 && height > 640 && <BottomResourceViewer />}
          </View>
        </View>
        <View style={styles.bottomComponentContainer}>
          <ExplorationBottomComponent />
        </View>
        <KeyboardListener callback={onKeyboard} />
      </View>
    ),
    [
      playerContextRef,
      currentExplorationBottomTab,
      endExploration,
      dungeonTitle,
      topContainer,
      theme,
      onKeyboard,
      width,
      height,
    ],
  );
}

export default function Exploration() {
  const playerContext = useContext(PlayerContext);

  const explorationStatus = getExplorationStatus(playerContext);

  if (explorationStatus == ExplorationStatus.None) {
    return <ExplorationNone />;
  }

  // We are exploring, reviving, or in combat
  return <ExplorationMain />;
}

const styles = StyleSheet.create({
  wholeContainer: { flex: 1 },
  container: {
    flex: 1,
    padding: 8,
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 16,
    height: 40,
  },
  arrowBack: {
    marginRight: 8,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  checkmark: {
    marginRight: 8,
  },
  enemyContainer: {
    marginBottom: 8,
  },
  playerContainer: {
    marginTop: 8,
  },
  summonContainer: {
    marginTop: 8,
  },
  playerMessageLogContainer: {
    flex: 1,
    padding: 8,
    borderColor: "#444",
    borderWidth: 1,
    marginTop: 8,
  },
  moveRow: {
    marginTop: 8,
  },
  exitButtonContainer: { marginRight: 4 },
  explorationStatusContainer: {
    flex: 1,
    maxHeight: 210,
  },
  restOfScreen: {
    flex: 1,
    justifyContent: "space-between",
    marginBottom: 48,
  },
  bottomComponentContainer: {
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "100%",
  },
  dungeonButton: {
    marginTop: 8,
  },
  gradient: {
    left: 0,
    top: 0,
    width: "100%",
    height: "60%",
    position: "absolute",
    opacity: 0.3,
  },
  noneContent: {
    paddingBottom: 32,
  },
  noneDungeonContentsContainer: {
    marginTop: 8,
  },
  noneRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 4,
  },
  noneStatusRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  noneFloorText: {
    fontWeight: "bold",
  },
  noneIcon: {
    marginHorizontal: 4,
  },
  smallButtonTitle: {
    fontSize: 12,
  },
  runAway: {
    marginTop: 4,
  },
  unlockBossContainer: { flex: 1, justifyContent: "flex-start" },
  unlockBossTextContainer: { flex: 1 },
  accessibilityHidden: {
    width: 1,
    height: 1,
    position: "absolute",
    left: 0,
    top: 0,
  },
  pauseExplorationButton: {
    marginTop: 16,
    alignSelf: "flex-start",
  },
});
